import { useEffect, useRef } from 'react';
import { overlayHandledPopstate } from '@/hooks/useBackClose';

/**
 * useUnsavedGuard — protect in-progress work from accidental loss.
 *
 * While `active` is true it installs two guards, then tears them down when
 * `active` flips false (e.g. after a save, or after the user confirms leaving):
 *
 *   1. beforeunload — the native browser prompt on tab close, refresh, or a
 *      hard URL navigation. Reliable everywhere; cannot be styled or bypassed.
 *
 *   2. A history "trap" for the browser Back button. On mount it pushes one
 *      same-URL sentinel entry so the first Back press is absorbed instead of
 *      yanking the user off the page; each subsequent Back press re-arms the
 *      sentinel and fires `onBackAttempt` so the caller can show a styled
 *      "discard changes?" dialog. Same-URL entries keep React Router's route
 *      unchanged, so this doesn't fight the router.
 *
 * The app uses <BrowserRouter> (not a data router), so React Router's
 * useBlocker isn't available — this hook is the dependency-free equivalent
 * for a single guarded surface. NOTE: it intercepts Back/refresh/close, not
 * in-app link clicks (e.g. sidebar nav); guard those via the caller's own
 * exit handler.
 *
 * @param {boolean} active         Whether there are unsaved changes to protect.
 * @param {() => void} onBackAttempt Called when the user presses Back while active.
 */
export function useUnsavedGuard(active, onBackAttempt) {
  const onBackRef = useRef(onBackAttempt);
  onBackRef.current = onBackAttempt;

  // (1) Native prompt for refresh / tab close / hard navigation.
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = ''; // Chrome requires returnValue to be set.
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [active]);

  // (2) Back-button trap → styled confirm instead of navigating away.
  useEffect(() => {
    if (!active) return undefined;
    if (typeof window === 'undefined' || !window.history) return undefined;

    window.history.pushState({ koGuard: true }, '');
    const onPop = (e) => {
      // If the overlay dispatcher (useBackClose) already spent this Back
      // press closing a lightbox/sheet, the entry popped was the overlay's
      // sentinel, not ours — ours is intact, so stay quiet.
      if (overlayHandledPopstate(e)) return;
      // A Back press consumed our sentinel — re-arm it so we stay put, then
      // let the caller ask the user what to do.
      window.history.pushState({ koGuard: true }, '');
      if (typeof onBackRef.current === 'function') onBackRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [active]);
}

export default useUnsavedGuard;
